/** Analyze a private offline render before CookUp AI commits it to the DAW. */

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const db = (value) => (value > 0 ? 20 * Math.log10(value) : -120);

export function analyzeRenderedMix(buffer, { blockSeconds = 0.25 } = {}) {
  const channels = Array.from(
    { length: Number(buffer?.numberOfChannels) || 0 },
    (_, channel) => buffer.getChannelData(channel)
  );
  const sampleRate = Number(buffer?.sampleRate) || 48000;
  const length = Math.min(...channels.map((channel) => channel.length));
  if (!channels.length || !Number.isFinite(length) || length <= 0) {
    return { passed: false, issues: ['The private mix render contains no audio data.'], metrics: {} };
  }

  let peak = 0;
  let squares = 0;
  let sum = 0;
  let clipped = 0;
  let stereoCross = 0;
  let leftSquares = 0;
  let rightSquares = 0;
  let sideSquares = 0;
  let midSquares = 0;
  const blockSize = Math.max(1, Math.round(sampleRate * blockSeconds));
  const blockSquares = new Float64Array(Math.ceil(length / blockSize));

  for (let index = 0; index < length; index += 1) {
    let mono = 0;
    channels.forEach((channel) => { mono += channel[index] || 0; });
    mono /= channels.length;
    const absolute = Math.abs(mono);
    peak = Math.max(peak, absolute);
    squares += mono * mono;
    sum += mono;
    if (absolute >= 0.999) clipped += 1;
    blockSquares[Math.floor(index / blockSize)] += mono * mono;
    if (channels.length >= 2) {
      const left = channels[0][index] || 0;
      const right = channels[1][index] || 0;
      stereoCross += left * right;
      leftSquares += left * left;
      rightSquares += right * right;
      const mid = (left + right) * 0.5;
      const side = (left - right) * 0.5;
      midSquares += mid * mid;
      sideSquares += side * side;
    }
  }

  const rms = Math.sqrt(squares / length);
  const blockRms = [...blockSquares].map((value, block) => {
    const count = Math.min(blockSize, length - block * blockSize);
    return Math.sqrt(value / Math.max(1, count));
  });
  const activeBlocks = blockRms.filter((value) => value >= 0.002);
  const silenceRatio = 1 - (activeBlocks.length / Math.max(1, blockRms.length));
  const stereoCorrelation = channels.length < 2
    ? 1
    : stereoCross / Math.max(1e-12, Math.sqrt(leftSquares * rightSquares));
  const leftRms = Math.sqrt(leftSquares / Math.max(1, length));
  const rightRms = Math.sqrt(rightSquares / Math.max(1, length));
  const channelBalanceDb = channels.length < 2
    ? 0
    : Math.abs(db(Math.max(leftRms, 1e-12)) - db(Math.max(rightRms, 1e-12)));
  const stereoWidthRatio = channels.length < 2
    ? 0
    : Math.sqrt(sideSquares / Math.max(1e-12, midSquares));
  const issues = [];
  const warnings = [];
  if (rms < 0.002) issues.push('The private render is effectively silent. Repair missing or unloaded instrument and drum sources.');
  const clippedSampleRatio = clipped / length;
  // A legal floating-point Web Audio mix may cross 0 dBFS briefly before the
  // master safety stage. One transient touching full scale is not distortion.
  // Refuse only sustained clipping, or a materially overloaded floating mix.
  if (clippedSampleRatio > 0.01 || (peak > 1.35 && clippedSampleRatio > 0.0005)) {
    issues.push('The private render clips. Lower competing track levels and leave clean master headroom.');
  }
  if (Math.abs(sum / length) > 0.02) issues.push('The private render has excessive DC offset, usually caused by a broken source or signal stage.');
  if (rms >= 0.01 && db(peak / rms) < 2.5) issues.push('The private render has almost no transient headroom. Reduce over-compression or stacked levels.');
  if (silenceRatio > 0.72) issues.push('Most of the arranged song renders silent. Extend actual musical material through the intended sections.');
  if (stereoCorrelation < -0.15) warnings.push('The mix has strong negative stereo correlation and may lose important elements in mono.');
  if (channelBalanceDb > 4) warnings.push('The mix is heavily weighted to one stereo channel. Check panning and source balance.');
  if (rms >= 0.002 && peak < 0.063) warnings.push('The mix is unusually quiet. Check gain staging before export.');

  const activeDb = activeBlocks.map(db);
  const peakDbfs = db(peak);
  const crestDb = db(peak / Math.max(rms, 1e-12));
  const headroomScore = clamp(1 - Math.abs(peakDbfs + 5.5) / 12, 0, 1);
  const dynamicsScore = clamp(1 - Math.abs(crestDb - 10) / 12, 0, 1);
  const coverageScore = clamp(1 - silenceRatio / 0.72, 0, 1);
  const stereoScore = channels.length < 2 ? 0.8 : clamp((stereoCorrelation + 0.2) / 0.7, 0, 1);
  const integrityScore = clamp(1 - clippedSampleRatio * 50 - Math.abs(sum / length) * 12, 0, 1);
  const qualityScore = Math.round(1000 * (
    headroomScore * 0.25
    + dynamicsScore * 0.22
    + coverageScore * 0.23
    + stereoScore * 0.12
    + integrityScore * 0.18
  )) / 10;
  return {
    passed: issues.length === 0,
    issues,
    warnings,
    qualityScore,
    metrics: {
      peakDbfs: Number(peakDbfs.toFixed(2)),
      rmsDbfs: Number(db(rms).toFixed(2)),
      crestDb: Number(crestDb.toFixed(2)),
      clippedSampleRatio: Number(clippedSampleRatio.toFixed(6)),
      silenceRatio: Number(silenceRatio.toFixed(3)),
      dcOffset: Number((sum / length).toFixed(5)),
      stereoCorrelation: Number(clamp(stereoCorrelation, -1, 1).toFixed(3)),
      stereoWidthRatio: Number(stereoWidthRatio.toFixed(3)),
      channelBalanceDb: Number(channelBalanceDb.toFixed(2)),
      activeLoudnessRangeDb: activeDb.length
        ? Number((Math.max(...activeDb) - Math.min(...activeDb)).toFixed(2))
        : 0
    }
  };
}

export default analyzeRenderedMix;
